import React, { Component } from "react";
import { injectIntl, defineMessages } from "react-intl";
import validator from "validator";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Alert,
} from "reactstrap";
import keys from "lodash/keys";
import { optionsEnum, getBrowserLocale } from "../Utils/SupportUtils";

const messages = defineMessages({
  name: {
    id: "SupportForm.name",
    defaultMessage: "Name",
  },
  surname: {
    id: "SupportForm.surname",
    defaultMessage: "Surname",
  },
  company: {
    id: "SupportForm.company",
    defaultMessage: "Company",
  },
  email: {
    id: "SupportForm.email",
    defaultMessage: "Email",
  },
  mobilePhone: {
    id: "SupportForm.mobilePhone",
    defaultMessage: "Mobile phone",
  },
  phoneNumber: {
    id: "SupportForm.phoneNumber",
    defaultMessage: "Phone number",
  },
  description: {
    id: "SupportForm.description",
    defaultMessage: "Description",
  },
  nameError: {
    id: "SupportForm.nameError",
    defaultMessage: "Name is required.",
  },
  phoneNumberError: {
    id: "SupportForm.phoneNumberError",
    defaultMessage: "Phone number is required.",
  },
  positiveNumbersError: {
    id: "SupportForm.positiveNumbersError",
    defaultMessage: "Only positive numbers are allowed.",
  },
  companyVatTaxError: {
    id: "SupportForm.companyVatTaxError",
    defaultMessage: "Company or VAT number/tax code must be filled.",
  },
  emailRequiredError: {
    id: "SupportForm.emailError",
    defaultMessage: "Email is required.",
  },
  emailNotValid: {
    id: "SupportForm.emailNotValid",
    defaultMessage: "You must enter a valid email address.",
  },
  mobileError: {
    id: "SupportForm.mobileError",
    defaultMessage: "Mobile phone is required.",
  },
  privacyPolicy: {
    id: "SupportForm.privacyPolicy",
    defaultMessage:
      "SENDING the message, the interested party declares to have READ AND UNDERSTAND THE PRIVACY POLICY OF THE SITE and expresses his consent to the processing of sensitive/judicial data possibly reported in the text of the message for the sole purpose of satisfying his own requests.",
  },
  privacyPolicyError: {
    id: "SupportForm.privacyPolicyError",
    defaultMessage: "You must accept the privacy policy before continuing.",
  },
  submitButton: {
    id: "SupportForm.submitButton",
    defaultMessage: "Submit",
  },
  enterInformation: {
    id: "SupportForm.enterInformation",
    defaultMessage: "Please enter your information",
  },
  backButton: {
    id: "SupportForm.backButton",
    defaultMessage: "Back",
  },
  vatTaxCode: {
    id: "SupportForm.label.vatTaxCode",
    defaultMessage: "VAT number/tax code",
  },
  errorVatTaxCode: {
    id: "SupportForm.error.errorVatTaxCode",
    defaultMessage: "You must enter a valid VAT number or tax code",
  },
  mobilePhoneNotCorrect: {
    id: "SupportForm.error.mobilePhoneNotCorrect",
    defaultMessage: "Mobile number is not correct.",
  },
  descriptionError: {
    id: "SupportForm.error.descriptionError",
    defaultMessage: "Description is too long.",
  },
  selectedInformation: {
    id: "SupportForm.selectedInformation",
    defaultMessage: "You are searching information for:",
  },
});

const FISCAL_CODE_REGEX =
  "^[a-zA-Z]{6}[0-9]{2}[a-zA-Z][0-9]{2}[a-zA-Z][0-9]{3}[a-zA-Z]$";
const VAT_NUMBER_REGEX = "^[0-9]{11}$";
const PHONE_NUMBER_REGEX = /^(\+)?\d{4,15}$/;
const POSITIVE_NUMBERS_REGEX = /^\d{1,}$/;

class SupportForm extends Component {
  state = {
    data: {
      name: "",
      surname: "",
      company: "",
      email: "",
      mobilePhone: "",
      phoneNumber: "",
      description: "",
      vatTaxCode: null,
      sendInformation: false,
      privacyPolicy: false,
    },
    errors: {},
    alertOpen: false,
  };

  componentDidUpdate(prevProps) {
    if (prevProps.contactOption !== this.props.contactOption) {
      this.setState({
        ...this.state,
        errors: {},
      });
    }
  }

  submit = (event) => {
    event.preventDefault();
    const errors = this.validate();
    this.setState({
      ...this.state,
      errors,
    });
    const contactData = {
      name: `${this.state.data.name} ${this.state.data.surname}`,
      companyName: this.state.data.company,
      vatTaxCode: this.state.data.vatTaxCode,
      phone: this.state.data.phoneNumber,
      mobile: this.state.data.mobilePhone,
      email: this.state.data.email,
      description: this.state.data.description,
    };
    if (keys(errors).length === 0) {
      this.props.onSubmit(contactData);
      !this.state.alertOpen && this.toggleAlert();
    }
  };

  validate = () => {
    const {
      data: {
        name,
        company,
        email,
        mobilePhone,
        phoneNumber,
        privacyPolicy,
        vatTaxCode,
      },
    } = this.state;

    const errors = {};
    if (!name) {
      errors.name = this.props.intl.formatMessage(messages.nameError);
    }
    if (!phoneNumber) {
      errors.phoneNumber = this.props.intl.formatMessage(
        messages.phoneNumberError
      );
    }
    if (
      phoneNumber &&
      !validator.matches(phoneNumber, POSITIVE_NUMBERS_REGEX)
    ) {
      errors.phoneNumber = this.props.intl.formatMessage(
        messages.positiveNumbersError
      );
    }
    if (this.props.contactOption === optionsEnum.EMAIL && !email) {
      errors.email = this.props.intl.formatMessage(messages.emailRequiredError);
    }
    if (email && !validator.isEmail(email)) {
      errors.email = this.props.intl.formatMessage(messages.emailNotValid);
    }
    if (this.props.contactOption === optionsEnum.PHONE && !mobilePhone) {
      errors.mobilePhone = this.props.intl.formatMessage(messages.mobileError);
    }
    if (!company && !vatTaxCode) {
      errors.companyVatTax = this.props.intl.formatMessage(
        messages.companyVatTaxError
      );
    }
    if (!privacyPolicy) {
      errors.privacyPolicyError = this.props.intl.formatMessage(
        messages.privacyPolicyError
      );
    }
    if (
      vatTaxCode &&
      !validator.matches(vatTaxCode, FISCAL_CODE_REGEX) &&
      !validator.matches(vatTaxCode, VAT_NUMBER_REGEX)
    ) {
      errors.vatTaxCode = this.props.intl.formatMessage(
        messages.errorVatTaxCode
      );
    }
    if (mobilePhone && !validator.matches(mobilePhone, PHONE_NUMBER_REGEX)) {
      errors.mobilePhone = this.props.intl.formatMessage(
        messages.mobilePhoneNotCorrect
      );
    }
    return errors;
  };

  handleChange = (event) => {
    console.log(event.target.id);
    console.log(event.target.id === "description");

    if (event.target.id === "description" && event.target.value.length > 5000) {
      this.setState({
        ...this.state,
        errors: {
          description: this.props.intl.formatMessage(messages.descriptionError),
        },
      });
    } else {
      this.setState({
        ...this.state,
        data: {
          ...this.state.data,
          [event.target.id]: event.target.value,
        },
        errors: {},
        alertOpen: false,
      });
    }
  };

  handleCheckbox = (event) => {
    this.setState({
      ...this.state,
      data: {
        ...this.state.data,
        [event.target.id]: !this.state.data[event.target.id],
      },
      errors: {},
      alertOpen: false,
    });
  };

  toggleAlert = () => {
    this.setState({
      ...this.state,
      alertOpen: !this.state.alertOpen,
    });
  };

  render() {
    const {
      contactOption,
      sendSupportDataError,
      toggleForm,
      submitButtonStyles,
      backButtonStyles,
      bodyStyle,
      errorMessage,
      historyNodes,
      intl: { formatMessage },
    } = this.props;
    const { errors, alertOpen } = this.state;
    const language = getBrowserLocale();
    const isErrorAlertOpen = sendSupportDataError && alertOpen;

    const sbStyles = submitButtonStyles
      ? {
          backgroundColor: submitButtonStyles.backgroundColor,
          border: `1px solid ${submitButtonStyles.borderColor}`,
          color: submitButtonStyles.color,
        }
      : {};

    const bbStyles = backButtonStyles
      ? {
          backgroundColor: backButtonStyles.backgroundColor,
          border: `1px solid ${backButtonStyles.borderColor}`,
          color: backButtonStyles.color,
        }
      : {};

    return (
      <>
        <div className="text-left">
          <Button style={bbStyles} onClick={() => toggleForm(null)}>
            {formatMessage(messages.backButton)}
          </Button>
        </div>
        <div
          className="text-center mb-4"
          style={{ color: bodyStyle.color, fontFamily: "Fresca" }}
        >
          <p style={{ fontSize: "1.5em", margin: "0" }}>
            {formatMessage(messages.selectedInformation)}
          </p>
          {historyNodes.map((el, index) => {
            return (
              <span key={el.nodeid}>
                {el.label[language]}
                {index < historyNodes.length - 1 && " > "}
              </span>
            );
          })}
        </div>
        <h2
          className="text-center mt-4"
          style={{ color: bodyStyle.color, fontWeight: 300 }}
        >
          {formatMessage(messages.enterInformation)}
        </h2>
        <div className="border rounded-bottom p-4 mb-4">
          <Form onSubmit={this.submit} style={{ color: bodyStyle.color }}>
            {sendSupportDataError && (
              <Alert
                color="danger"
                isOpen={isErrorAlertOpen}
                toggle={this.toggleAlert}
              >
                {errorMessage[language]}
              </Alert>
            )}
            <div className="row">
              <div className="col-md-6">
                <FormGroup>
                  <Label for="name">{formatMessage(messages.name)} *</Label>
                  <Input
                    id="name"
                    invalid={errors.name ? true : false}
                    onChange={this.handleChange}
                  />
                  <FormFeedback>{errors.name}</FormFeedback>
                </FormGroup>
              </div>
              <div className="col-md-6">
                <FormGroup>
                  <Label for="surname">{formatMessage(messages.surname)}</Label>
                  <Input id="surname" onChange={this.handleChange} />
                  <FormFeedback>{errors.surname}</FormFeedback>
                </FormGroup>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <FormGroup>
                  <Label for="company">{formatMessage(messages.company)}</Label>
                  <Input
                    id="company"
                    invalid={errors.companyVatTax ? true : false}
                    onChange={this.handleChange}
                  />
                  <FormFeedback>{errors.companyVatTax}</FormFeedback>
                </FormGroup>
              </div>
              <div className="col-md-6">
                <FormGroup>
                  <Label for="VAT number/tax code">
                    {formatMessage(messages.vatTaxCode)}
                  </Label>
                  <Input
                    id="vatTaxCode"
                    invalid={
                      errors.companyVatTax || errors.vatTaxCode ? true : false
                    }
                    onChange={this.handleChange}
                  />
                  <FormFeedback>
                    {errors.companyVatTax || errors.vatTaxCode}
                  </FormFeedback>
                </FormGroup>
              </div>
            </div>

            <div className="row">
              <div className="col-md-4">
                <FormGroup>
                  <Label for="phoneNumber">
                    {formatMessage(messages.phoneNumber)} *
                  </Label>
                  <Input
                    id="phoneNumber"
                    invalid={errors.phoneNumber ? true : false}
                    onChange={this.handleChange}
                  />
                  <FormFeedback>{errors.phoneNumber}</FormFeedback>
                </FormGroup>
              </div>
              <div className="col-md-4">
                <FormGroup>
                  <Label for="mobilePhone">
                    {formatMessage(messages.mobilePhone)}
                    {contactOption === optionsEnum.PHONE ? " *" : ""}
                  </Label>
                  <Input
                    id="mobilePhone"
                    invalid={errors.mobilePhone ? true : false}
                    onChange={this.handleChange}
                  />
                  <FormFeedback>{errors.mobilePhone}</FormFeedback>
                </FormGroup>
              </div>

              <div className="col-md-4">
                <FormGroup>
                  <Label for="email">
                    {formatMessage(messages.email)}
                    {contactOption === optionsEnum.EMAIL ? " *" : ""}
                  </Label>
                  <Input
                    id="email"
                    invalid={errors.email ? true : false}
                    onChange={this.handleChange}
                  />
                  <FormFeedback>{errors.email}</FormFeedback>
                </FormGroup>
              </div>
            </div>
            <FormGroup>
              <Label for="description">
                {formatMessage(messages.description)}
              </Label>
              <Input
                type="textarea"
                id="description"
                invalid={errors.description}
                onChange={this.handleChange}
              />
              <FormFeedback>{errors.description}</FormFeedback>
            </FormGroup>
            <FormGroup check>
              <Label check for="privacyPolicy">
                <Input
                  type="checkbox"
                  id="privacyPolicy"
                  invalid={errors.privacyPolicyError ? true : false}
                  onChange={this.handleCheckbox}
                />
                {formatMessage(messages.privacyPolicy)}
                <FormFeedback>{errors.privacyPolicyError}</FormFeedback>
              </Label>
            </FormGroup>
            <div className="text-center mt-4">
              <Button style={sbStyles} type="submit">
                {formatMessage(messages.submitButton)}
              </Button>
            </div>
          </Form>
        </div>
      </>
    );
  }
}

export default injectIntl(SupportForm);
